import { useEffect, useState, useCallback, useMemo, useRef } from "react";

import { DEFAULT_PAGINATION_LIMIT } from "../../../../../globalConstants";
import { useQueryParams } from "../../../../hooks/useQueryParams";
import useService from "../../../../hooks/useService";
import { defaultFilterParams } from "../utils/filterConfig";

const ACTION_ITEMS_QUERY_OPTIONS = { debounceMs: 0, replaceNavigation: true };

export function useActionableItems({ caseId = null } = {}) {
  const actionItemRepository = useService("repositories/actionable-items-repository");

  const { filters, bulkUpdateFilters } = useQueryParams(ACTION_ITEMS_QUERY_OPTIONS);
  const [actionItems, setActionItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resultCount, setResultCount] = useState({ total: 0, current: 0 });
  const requestIdRef = useRef(0);

  const filterParams = useMemo(() => {
    return Object.fromEntries(Array.from(filters.entries()).filter(([_, value]) => Boolean(value)));
  }, [filters]);

  const offset = parseInt(filters.get("offset") || "0", 10);
  const limit = parseInt(filters.get("limit") || DEFAULT_PAGINATION_LIMIT.toString(), 10);
  const currentPage = Math.floor(offset / limit) + 1;

  const ordering = filters.get("ordering") || "due_date";

  useEffect(() => {
    const updates = Object.entries(defaultFilterParams || {}).filter(([key, _value]) => {
      const currentValue = filters.get(key);
      return currentValue === null || currentValue === undefined;
    });

    if (updates.length > 0) {
      bulkUpdateFilters(updates);
    }
  }, []);

  const fetchActionItems = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);

    try {
      const queryParams = {
        ...filterParams,
        ordering,
        limit,
        offset,
        ...(caseId ? { on_case: caseId } : {}),
      };

      const response = await actionItemRepository.query(queryParams);
      const totalCount = await actionItemRepository.getTotalCount();

      if (requestId !== requestIdRef.current) {
        return;
      }

      setActionItems(response);
      setResultCount({
        total: totalCount,
        current: response.length,
      });
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      console.error("Error fetching action items:", err);
      setActionItems([]);
      setResultCount({ total: 0, current: 0 });
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [actionItemRepository, filterParams, ordering, limit, offset, caseId]);

  useEffect(() => {
    fetchActionItems();
  }, [fetchActionItems]);

  const refreshActionItems = useCallback(() => {
    fetchActionItems();
  }, [fetchActionItems]);

  return {
    actionItems,
    loading,
    resultCount,
    currentPage,
    totalPages: Math.ceil(resultCount.total / limit),
    fetchActionItems: refreshActionItems,
  };
}
